import { IQuestionConfig } from 'src/app/ui-testrunner/models';
import {
  IAssessmentFrameworkDetail,
  IQuadrantConstraint,
  TestFormConstructionMethod,
} from '../../item-set-editor/models/assessment-framework';
import { EFilterMethods } from '../data/audits';
import { checkPropCondVal } from './content-analysis';

/**
 * Field test resolved from **placement** in the test design rather than from the item's
 * `FT` parameter.
 *
 * `CHANGE_SCALE_AUDIT/CHANGE_SCALE_4_NOT_FT` reads `FT IS_TRUTHY` off the item, so a screen
 * that sits in the FT section but never had the parameter set is not in its population at
 * all. Passages compound it — they are dropped before any scored-item audit runs. Placement
 * is the signal that cannot be forgotten by an author, so the FT placement audit keys off
 * the framework structure instead.
 */

/** Section / quadrant labels that mark a field test partition, EN and FR. */
export const FT_PLACEMENT_LABEL_PATTERN = /(^|[^a-z])ft([^a-z]|$)|field[\s_-]*(test|trial)|essai/i;

interface IFtPlacementQuestionRef {
  id?: number | string;
}

/** The slice of the framework placement resolution reads. */
export type IFtPlacementFramework = Partial<Pick<
  IAssessmentFrameworkDetail,
  'testFormType' | 'partitions' | 'quadrants' | 'testlets' | 'sectionItems'
>>;

export const isFtPlacementLabel = (label?: string): boolean =>
  !!label && FT_PLACEMENT_LABEL_PATTERN.test(label);

/** `FT_Pool` present, or `FT` set to a value the audit engine treats as truthy. */
export const isFtQuadrantConstraint = (constraint?: IQuadrantConstraint): boolean => {
  if (!constraint) { return false; }
  if (constraint.param === 'FT_Pool') { return true; }
  return constraint.param === 'FT' && checkPropCondVal(constraint.val, EFilterMethods.IS_TRUTHY);
};

export const getFtPartitionIds = (fmrk?: IFtPlacementFramework): Set<number> => {
  const ids = new Set<number>();
  for (const partition of fmrk?.partitions ?? []) {
    if (isFtPlacementLabel(partition?.description)) { ids.add(+partition.id); }
  }
  return ids;
};

export const getFtQuadrantIds = (
  fmrk?: IFtPlacementFramework,
  ftPartitionIds: Set<number> = getFtPartitionIds(fmrk),
): Set<number> => {
  const ids = new Set<number>();
  for (const quadrant of fmrk?.quadrants ?? []) {
    if (!quadrant) { continue; }
    const isFt = (quadrant.constraints ?? []).some(isFtQuadrantConstraint)
      || isFtPlacementLabel(quadrant.description)
      || ftPartitionIds.has(+quadrant.section);
    if (isFt) { ids.add(+quadrant.id); }
  }
  return ids;
};

/**
 * Question ids placed in a field test section or testlet. Disabled testlets are out —
 * they are not published, so they cannot carry a live parameter defect.
 */
export const getFtPlacedQuestionIds = (fmrk?: IFtPlacementFramework): Set<number> => {
  const questionIds = new Set<number>();
  if (!fmrk) { return questionIds; }

  const ftPartitionIds = getFtPartitionIds(fmrk);
  const ftQuadrantIds = getFtQuadrantIds(fmrk, ftPartitionIds);

  const collect = (questions?: IFtPlacementQuestionRef[]) => {
    for (const question of questions ?? []) {
      if (question?.id != null) { questionIds.add(+question.id); }
    }
  };

  const isTestletDesign = fmrk.testFormType === TestFormConstructionMethod.TLOFT
    || fmrk.testFormType === TestFormConstructionMethod.MSCAT;

  if (isTestletDesign) {
    for (const testlet of fmrk.testlets ?? []) {
      if (!testlet || testlet.isDisabled) { continue; }
      if (ftQuadrantIds.has(+testlet.quadrant) || ftPartitionIds.has(+testlet.section)) {
        collect(testlet.questions);
      }
    }
    return questionIds;
  }

  for (const partitionId of ftPartitionIds) {
    collect(fmrk.sectionItems?.[partitionId]?.questions ?? fmrk.sectionItems?.[`${partitionId}`]?.questions);
  }
  return questionIds;
};

export type TFtPlacementSide = 'IN_FT' | 'OUTSIDE_FT';

export interface IFtPlacementCheckParams {
  /** Which side of the placement boundary the check looks at. Defaults to `IN_FT`. */
  placement?: TFtPlacementSide;
  /** Item param to test once placement matches; omit to flag on placement alone. */
  targetProp?: string;
  filterMethod?: EFilterMethods;
  filterValue?: any;
  /** `ItemType` values left out, e.g. `LW`, which carries its own scale conventions. */
  excludeItemTypes?: string[];
  onlyPassages?: boolean;
}

/** Shared by the FT_PLACEMENT_PARAM_CHECK audit function; kept here so it is testable without the controller. */
export const filterByFtPlacementParam = <T extends IQuestionConfig>(
  questions: T[],
  ftPlacedQuestionIds: Set<number>,
  params: IFtPlacementCheckParams = {},
): T[] => {
  const { placement = 'IN_FT', targetProp, filterMethod, filterValue, excludeItemTypes = [], onlyPassages } = params;
  const excludedTypes = new Set((excludeItemTypes ?? []).map(type => `${type}`.trim().toUpperCase()));

  return (questions ?? []).filter(question => {
    if (!question) { return false; }
    if (onlyPassages && !question.isReadingSelectionPage) { return false; }
    if (ftPlacedQuestionIds.has(+question.id) !== (placement === 'IN_FT')) { return false; }
    const itemType = `${question.meta?.['ItemType'] ?? ''}`.trim().toUpperCase();
    if (itemType && excludedTypes.has(itemType)) { return false; }
    if (!targetProp) { return true; }
    return checkPropCondVal(question.meta?.[targetProp], filterMethod, filterValue);
  });
};
